import { Button, Dialog, Divider, Intent, Spinner, SpinnerSize } from "@blueprintjs/core";
import {
  ButtonContainer,
  FGCustomInput,
  FGCustomPanel,
  FGSelectInput,
  FieldGroup,
  showError,
  showInfo,
  showSuccess,
  useApiEffect,
  useFGFormik,
} from "nsitools-react";
import * as React from "react";
import { Link, useParams } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import styled from "styled-components";

import { PrintButton, SmallFormGenerator } from "..";
import {
  EPlatform,
  ERequestState,
  ERequestType,
  ErrorDetails,
  ImportRequestApi,
  RequestApi,
  RequestInfoDto,
} from "../../api";
import { useAuth, useDialog, useEventsContext, useGlobalData } from "../../contexts";
import { ERoutes } from "../../ERoutes";
import { useApiService, useTl } from "../../hooks";
import { useReferential } from "../../hooks/useReferential";
import { ETLCodes } from "../../locales";
import { ArePuppiesDirty } from "../../pages/request/broodRequest/item/PuppiesTab";
import { getRouteStr } from "../../utils/RequestRouteUtil";
import { getSearchRouteStr } from "../../utils/RequestSearchRouteUtil";
import { RequestCancelCommentDialog } from "./RequestCancelCommentDialog";

export interface IRequestStickyMenuProps {
  requestId: number;
  requestInfo?: RequestInfoDto;
  refreshFunc: (dto?: any) => Promise<any> | any;
  closeFunc: (dto?: any) => Promise<any> | any;
  isLoading?: (loading: boolean) => void;
}
const Container = styled.div`
  padding: 0.5rem;
  padding-bottom: 3px;
  background-color: #a7d3bb;
  border-bottom: 2px solid #5c7080;
  vertical-align: middle;
  z-index: 10;
  /* display: flex; */
  /* flex-direction: row;
  justify-content: space-between; */
`;

const StyledButton = styled(Button)`
  margin-left: 1rem;
`;

const StyledRequestTitleContainer = styled.div`
  font-size: 15pt;
  font-weight: bold;
  min-height: 35px;
`;

const FloatButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StateContainer = styled.div`
  display: flex;
  align-items: flex-start;
  & > * {
    justify-content: flex-start !important;
  }
`;

export const RequestStickyMenu: React.FunctionComponent<IRequestStickyMenuProps> = ({
  requestId,
  requestInfo,
  refreshFunc,
  closeFunc,
  isLoading,
}) => {
  const puppiesDirtyRecoilValue = useRecoilValue(ArePuppiesDirty);
  const setArePuppiesDirty = useSetRecoilState(ArePuppiesDirty);
  const { tab } = useParams<{ tab: string }>();
  const { canBlockRequest } = useAuth();
  const { state } = useParams<{ state: string }>();
  const { currentLanguage } = useGlobalData();
  const { t, tUnsafe } = useTl();
  const api = useApiService(RequestApi);
  const importRequestApi = useApiService(ImportRequestApi);
  const { showDialogPromise } = useDialog();
  const { dispatchEvent } = useEventsContext();

  const [askInfosLoading, setAskInfosLoading] = React.useState(false);

  const isSubFile = React.useMemo(() => {
    return requestInfo?.masterRequestId > 0;
  }, [requestInfo]);

  // -------------------- GETTING DATA ------------------------------
  const [data, loadingData, , refresh, setData] = useApiEffect(
    React.useCallback(
      async () =>
        await api.getRequestMenu(isSubFile ? requestInfo?.parentRequestId : requestId, currentLanguage.languageId),
      [api, currentLanguage.languageId, isSubFile, requestId, requestInfo]
    ),
    [api, requestId, currentLanguage]
  );

  const [statuses, statusloading, _, refetch] = useReferential(
    (a) => a.getFluxStatuses(isSubFile ? requestInfo?.parentRequestId : requestId, currentLanguage.languageId),
    true,

    [requestId, currentLanguage.languageId, data]
  );

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();

  const onRequestRefreshEvent = React.useCallback(() => {
    console.log("event");
    refetch();
  }, [refresh]);

  React.useEffect(() => {
    subscribeToEvent("RefetchRequestStatuses", onRequestRefreshEvent);
    return () => unsubscribeEvent("RefetchRequestStatuses", onRequestRefreshEvent);
  }, [onRequestRefreshEvent, subscribeToEvent, unsubscribeEvent]);

  const [currentRequestStatus] = useApiEffect(() => api.getStatusCurrentRequest(requestId), [requestId]);
  const [editableStatus, setEditableStatus] = React.useState(
    data?.requestStatusCode !== ERequestState.CANCELLED && data?.requestStatusCode !== ERequestState.EXECUTED
  );
  const [blocked, setBlocked] = React.useState(data?.isBlocked);
  const [currentError, setCurrentError] = React.useState<ErrorDetails>();

  const updateBlockedStatus = React.useCallback(async () => {
    await api.updateRequestBlockedStatus(requestId);
    refreshFunc();
    refresh();
    dispatchEvent("RequestRefresh");
  }, [api, dispatchEvent, refresh, refreshFunc, requestId]);

  const isSyncWithParent = React.useMemo(() => {
    if (isSubFile) {
      return requestInfo?.requestStateCode === requestInfo?.masterRequestStateCode;
    }
    return true;
  }, [isSubFile, requestInfo]);

  React.useEffect(() => {
    console.log("CAN BLOCK?", canBlockRequest());
    if (data) {
      setEditableStatus(
        data.requestStatusCode !== ERequestState.CANCELLED && data.requestStatusCode !== ERequestState.EXECUTED
      );
      setBlocked(data.isBlocked);
    }
  }, [canBlockRequest, data]);

  const searchRoute = getSearchRouteStr(data?.requestTypeCode);

  const [saving, setSaving] = React.useState(false);

  const safeIsLoading = React.useCallback(
    (loading: boolean) => {
      if (isLoading !== undefined) {
        isLoading(loading);
      }
    },
    [isLoading]
  );

  const [cancelDialogOpen, setcancelDialogOpen] = React.useState(false);

  const mainSaveFn = React.useCallback(
    async (formikData) => {
      setSaving(true);
      safeIsLoading(false);

      try {
        //CHECK VERIFICATIONS AND GET NEXT STATUS
        const allVerificationsCorrect = await api.checkAllVerifications(formikData.requestId);
        const futureStatus = await api.getRequestStatus(formikData.requestStateId);

        if (!allVerificationsCorrect.value && futureStatus !== ERequestState.CANCELLED) {
          const dialogResult = await showDialogPromise({
            message: t(ETLCodes.NotAllVerificationsChecked),
            title: t(ETLCodes.VerificationMessage),
          });
          if (dialogResult !== "yes") {
            setSaving(false);
            safeIsLoading(false);
            return;
          }
        }

        //STATUS DIALOG CONFIRMATION
        let confirmationMessage = null;
        let confirmationTitle = null;

        switch (futureStatus) {
          case ERequestState.PAID:
            confirmationMessage = t(ETLCodes.PaymentConfirmationMessage);
            confirmationTitle = t(ETLCodes.PaymentStatusRequest);
            break;
          case ERequestState.CANCELLED:
            setcancelDialogOpen(true);
            return;
            break;
          case ERequestState.EXECUTED:
            confirmationMessage = t(ETLCodes.ExecuteConfirmationMessage);
            confirmationTitle = t(ETLCodes.ExecuteConfirmationTitle);
            break;
          case ERequestState.AWAITING_CATALOG || ERequestState.AUTHORIZED:
            confirmationMessage = t(ETLCodes.JudgeConfirmationMessage);
            confirmationTitle = t(ETLCodes.JudgeConfirmationTitle);
            break;
        }

        if (confirmationMessage && confirmationTitle) {
          const dialogResult = await showDialogPromise({
            message: confirmationMessage,
            title: confirmationTitle,
          });
          if (dialogResult !== "yes") {
            setSaving(false);
            safeIsLoading(false);
            return;
          }
        }

        await api.updateRequestStatus(data?.requestId, formikData.requestStateId, EPlatform.CARE4DOGS);
        var newStatus = await api.getRequestStatus(requestInfo?.parentRequestId);

        console.log("New status:", newStatus);
        console.log("Future status:", futureStatus);
        if (newStatus !== futureStatus && futureStatus === ERequestState.EXECUTED) {
          await closeFunc();
        }

        const newData = await api.getRequestMenu(requestId, currentLanguage.languageId);
        setData(newData);
      } catch (err) {
        const details: ErrorDetails = JSON.parse(err.response);
        if (!!details.errorCode) {
          showError(tUnsafe(`BackendError_${details.errorCode}`));
        } else {
          showError(t(ETLCodes.Error));
        }
      }

      setSaving(false);
      safeIsLoading(false);
      await refresh();
      await refreshFunc();
      dispatchEvent("RequestRefresh");
    },
    [
      api,
      closeFunc,
      currentLanguage.languageId,
      data?.requestId,
      dispatchEvent,
      refresh,
      refreshFunc,
      requestId,
      requestInfo?.parentRequestId,
      safeIsLoading,
      setData,
      showDialogPromise,
      t,
      tUnsafe,
    ]
  );

  const manageError = React.useCallback(
    (error: any) => {
      const details: ErrorDetails = JSON.parse(error.response);
      if (details?.errorCode) {
        if (details?.errorCode === "NavError") {
          showSuccess(t(ETLCodes.SaveSuccess));
          refresh();
        }
        setCurrentError(details);
      } else {
        showError(t(ETLCodes.Error));
      }
      return;
    },
    [refresh, t]
  );

  React.useEffect(() => {
    if (currentError && currentError.errorCode) {
      showError(tUnsafe(`BackendError_${currentError.errorCode}`));
    }
  }, [currentError, tUnsafe]);

  const askInfos = React.useCallback(async () => {
    setAskInfosLoading(true);
    try {
      const allOK = await api.askInfos(isSubFile ? requestInfo?.parentRequestId : requestId);
      if (allOK) {
        showInfo(t(ETLCodes.AllRightNothingToSend));
      } else {
        showSuccess(t(ETLCodes.InfosSentByEmail));
      }
      setAskInfosLoading(false);
    } catch (error) {
      setAskInfosLoading(false);
      manageError(error);
    }
  }, [api, isSubFile, manageError, requestId, requestInfo?.parentRequestId, t]);

  const isStatusRequested = React.useMemo(
    () => currentRequestStatus === ERequestState.REQUESTED,
    [currentRequestStatus]
  );

  const isStatusDogValidation = React.useMemo(
    () => currentRequestStatus === ERequestState.DOG_VALIDATION,
    [currentRequestStatus]
  );

  const RequestTitle = React.useCallback(() => {
    if (isSubFile) {
      return (
        <StyledRequestTitleContainer>
          <Link to={getRouteStr(requestInfo?.masterRequestTypeCode, requestInfo?.masterSubRequestId, "view")}>
            {requestInfo.masterRequestNumber}
          </Link>
          <i
            style={{
              marginLeft: "0.5rem",
              marginRight: "0.5rem",
              paddingTop: "0.3rem",
            }}
            className="fas fa-angle-right"
          />
          {requestInfo?.requestId === 0 ? t(ETLCodes.New) : data?.requestDescription}
        </StyledRequestTitleContainer>
      );
    } else {
      return (
        <StyledRequestTitleContainer>
          {data?.requestDescription}
          {editableStatus && !blocked && (
            <i
              style={{
                marginLeft: "0.5rem",
                marginRight: "0.5rem",
                paddingTop: "0.3rem",
              }}
              className="fas fa-arrow-circle-right"
            />
          )}
        </StyledRequestTitleContainer>
      );
    }
  }, [blocked, data?.requestDescription, editableStatus, isSubFile, requestInfo, t]);
  const [printLoading, setprintLoading] = React.useState(false);
  const PrintCallback = React.useCallback(async () => {
    console.log("printing..");
    setprintLoading(true);
    try {
      console.log("trying to print");
      await importRequestApi.reprintDocument(requestInfo?.requestId);
    } catch {
      console.log("terror print");
      showError(t(ETLCodes.Error));
    }
    setprintLoading(false);
  }, [importRequestApi, requestInfo?.requestId, t]);

  const [uncancelRequestLoading, setuncancelRequestLoading] = React.useState(false);

  const UncancelRequest = React.useCallback(async () => {
    setuncancelRequestLoading(true);
    try {
      await api.uncancelRequest(data?.requestId);
      showSuccess(t(ETLCodes.Success));
      refresh();
    } catch {
      showError(t(ETLCodes.Error));
    } finally {
      setuncancelRequestLoading(false);
    }
  }, [api, data?.requestId, refresh, t]);

  const FloatButton = React.useCallback(() => {
    if (data?.requestStatusCode === ERequestState.EXECUTED && data?.requestTypeCode === ERequestType.ImportRequest) {
      return <PrintButton minimal={false} onClick={() => PrintCallback()} loading={printLoading} />;
    } else if (!(data?.requestStatusCode === ERequestState.EXECUTED) && (!isSubFile || !isSyncWithParent)) {
      return state === "edit" ? (
        <>
          {data?.requestStatusCode === ERequestState.CANCELLED ? null : (
            <StyledButton
              minimal={false}
              text={blocked ? t(ETLCodes.Unblock) : t(ETLCodes.Block)}
              intent={blocked ? Intent.PRIMARY : Intent.DANGER}
              onClick={updateBlockedStatus}
              disabled={!canBlockRequest()}
            />
          )}

          {!blocked && data?.requestStatusCode === ERequestState.CANCELLED && !!data?.previousRequestStatusId ? (
            <StyledButton
              minimal={false}
              text={t(ETLCodes.UnCancelRequest)}
              intent={"success"}
              onClick={UncancelRequest}
              loading={uncancelRequestLoading}
            />
          ) : null}
        </>
      ) : (
        <></>
      );
    } else {
      return <></>;
    }
  }, [
    PrintCallback,
    UncancelRequest,
    blocked,
    canBlockRequest,
    data?.previousRequestStatusId,
    data?.requestStatusCode,
    data?.requestTypeCode,
    isSubFile,
    isSyncWithParent,
    printLoading,
    state,
    t,
    uncancelRequestLoading,
    updateBlockedStatus,
  ]);

  const puppySpecificDirtyCheck = React.useMemo(() => {
    return tab === "puppies" && puppiesDirtyRecoilValue;
  }, [puppiesDirtyRecoilValue, tab]);

  React.useEffect(() => {
    if (ArePuppiesDirty && tab !== "puppies") {
      setArePuppiesDirty(false);
    }
  }, [setArePuppiesDirty, tab]);

  const formik = useFGFormik({
    initialValues: data,
    onSubmit: mainSaveFn,
  });

  const confirmCancel = React.useCallback(
    async (hasCanceled: boolean) => {
      if (hasCanceled) {
        await api.updateRequestStatus(requestId, formik?.values?.requestStateId, EPlatform.CARE4DOGS);
        const newData = await api.getRequestMenu(requestId, currentLanguage.languageId);
        setData(newData);
      }
      setSaving(false);
      safeIsLoading(false);
      setcancelDialogOpen(false);
    },
    [requestId, currentLanguage, formik]
  );

  return loadingData ? (
    <Spinner size={SpinnerSize.SMALL} />
  ) : (
    <Container>
      <SmallFormGenerator
        initialValues={data}
        onSubmit={mainSaveFn}
        editMode={true}
        loading={loadingData}
        saving={saving}
        hideButtons={true}
        enableDirtyCheck={false}
        formik={formik}
      >
        <FieldGroup columns={[8, 4]}>
          <FieldGroup>
            <FieldGroup columns={2}>
              <RequestTitle />
              <StateContainer>
                {editableStatus && !blocked && (!isSubFile || !isSyncWithParent) && (
                  <div style={{ flex: 1 }}>
                    <FGSelectInput
                      name="requestStateId"
                      items={statuses}
                      loading={statusloading}
                      backgroundColor={"WHITE"}
                    />
                  </div>
                )}
                {editableStatus && !blocked && (!isSubFile || !isSyncWithParent) && (
                  <FGCustomPanel>
                    {(ctx) => (
                      <ButtonContainer style={{ marginRight: "6rem" }}>
                        <StyledButton
                          text={t(ETLCodes.Change)}
                          intent={Intent.PRIMARY}
                          loading={saving}
                          type="submit"
                          disabled={
                            puppySpecificDirtyCheck ||
                            !ctx.formik.dirty ||
                            ctx.formik.values?.requestStateId === null ||
                            ctx.formik.values?.requestStateId === undefined
                          }
                        />
                      </ButtonContainer>
                    )}
                  </FGCustomPanel>
                )}
              </StateContainer>
            </FieldGroup>
            <FieldGroup columns={isStatusRequested ? 2 : 1}>
              <FGCustomInput name="requestStateId">
                {(ctx) => (
                  <Link target="_blank" to={searchRoute}>
                    {data?.requestType}
                  </Link>
                )}
              </FGCustomInput>
            </FieldGroup>
          </FieldGroup>
          <FieldGroup>
            <FloatButtonContainer>
              {(isStatusRequested || isStatusDogValidation) && (
                <Button
                  text={t(ETLCodes.Ask)}
                  onClick={() => askInfos()}
                  icon={<i className="fas fa-envelope" />}
                  intent={Intent.PRIMARY}
                  loading={askInfosLoading}
                />
              )}
              <FloatButton />
            </FloatButtonContainer>
          </FieldGroup>
        </FieldGroup>
        <FieldGroup>
          <Divider />
          <div style={{ marginLeft: "-120px " }}>
            <FieldGroup columns={3}>
              <FGCustomInput name="requestStateId" label={t(ETLCodes.Contact)}>
                {(ctx) => (
                  <Link target="_blank" to={`${ERoutes.people}/${data?.personId}/detail/view`}>
                    {data?.personName}
                  </Link>
                )}
              </FGCustomInput>

              {data?.personAddress && (
                <FGCustomInput name="requestStateId" label={t(ETLCodes.Address)}>
                  {(ctx) => <>{data?.personAddress}</>}
                </FGCustomInput>
              )}
              {data?.personEmail && (
                <FGCustomInput name="requestStateId" label={t(ETLCodes.Email)}>
                  {(ctx) => <a href={"mailto:" + data?.personEmail}>{data?.personEmail}</a>}
                </FGCustomInput>
              )}
            </FieldGroup>
          </div>
          <div style={{ marginLeft: "-87px " }}>
            {data?.managerId && (
              <FieldGroup>
                <FGCustomInput name="requestStateId" label={t(ETLCodes.Manager)}>
                  {(ctx) => <>{data?.manager}</>}
                </FGCustomInput>
              </FieldGroup>
            )}
          </div>
        </FieldGroup>
      </SmallFormGenerator>
      <Dialog isOpen={cancelDialogOpen} onClose={() => setcancelDialogOpen(false)} style={{ width: 800 }}>
        <RequestCancelCommentDialog requestId={requestId} onClose={confirmCancel} />
      </Dialog>
    </Container>
  );
};
